// Soundboard: Hilfen ohne React (lokale Lautstärke, Abspielen, Formatierung, Fehlertexte).
import type { SoundDto } from '@lagerfeuer/shared';
import { ApiError } from '../../lib/api';
import { errorMessage } from './format';

export const SOUNDBOARD_VOLUME_KEY = 'lf.soundboard.volume';
const DEFAULT_PERCENT = 100;

const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1);

/** Gemerkte lokale Lautstärke in Prozent (0–100). */
export function readSoundboardVolume(): number {
  try {
    const raw = localStorage.getItem(SOUNDBOARD_VOLUME_KEY);
    if (raw === null) return DEFAULT_PERCENT;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : DEFAULT_PERCENT;
  } catch {
    return DEFAULT_PERCENT;
  }
}

export function writeSoundboardVolume(percent: number): void {
  try {
    localStorage.setItem(SOUNDBOARD_VOLUME_KEY, String(Math.min(100, Math.max(0, Math.round(percent)))));
  } catch {
    /* Speicher nicht verfügbar (privater Modus) – still ignorieren */
  }
}

/** Effektive Wiedergabe-Lautstärke: Sound-Lautstärke × lokale Lautstärke, auf 0..1 begrenzt. */
export function effectiveVolume(soundVolume: number, localPercent: number = readSoundboardVolume()): number {
  return clamp01(clamp01(soundVolume) * (localPercent / 100));
}

/** Ein Audio abspielen; Autoplay-Sperren und fehlende Audio-Unterstützung still ignorieren. */
export function playAudio(url: string, volume: number): HTMLAudioElement | null {
  try {
    const audio = new Audio(url);
    audio.volume = clamp01(volume);
    const p = audio.play() as Promise<void> | undefined;
    p?.catch(() => {});
    return audio;
  } catch {
    return null;
  }
}

/** Eigener Sprachzustand, soweit das Soundboard ihn braucht. */
export interface SoundboardVoiceState {
  /** Kanal, mit dem dieser Client gerade verbunden ist (oder null). */
  channelId: string | null;
  /** Taub geschaltet: dann nichts abspielen. */
  deafened?: boolean;
}

// Ohne Anbindung durch das Voice-Paket gilt „nicht verbunden" – fremde Sounds werden dann nicht abgespielt.
const NOT_CONNECTED: SoundboardVoiceState = { channelId: null };
let voiceState: () => SoundboardVoiceState = () => NOT_CONNECTED;

/**
 * Das Voice-Paket meldet hier, mit welchem Sprachkanal der Client verbunden ist. Rückgabe: Abmeldefunktion.
 * Nötig, weil SOUNDBOARD_PLAY an alle Sockets mit Sichtrecht auf den Kanal geht, nicht nur an Verbundene.
 */
export function setSoundboardVoiceState(getter: () => SoundboardVoiceState): () => void {
  voiceState = getter;
  return () => {
    if (voiceState === getter) voiceState = () => NOT_CONNECTED;
    stopSoundboardSounds();
  };
}

/** Höchstens so viele fremde Sounds gleichzeitig; ein neuer stoppt den ältesten. */
export const SOUNDBOARD_MAX_CONCURRENT = 3;
const active: HTMLAudioElement[] = [];

function track(audio: HTMLAudioElement): void {
  active.push(audio);
  while (active.length > SOUNDBOARD_MAX_CONCURRENT) {
    const oldest = active.shift();
    try {
      oldest?.pause();
    } catch {
      /* ignorieren */
    }
  }
  const drop = () => {
    const i = active.indexOf(audio);
    if (i >= 0) active.splice(i, 1);
  };
  audio.addEventListener?.('ended', drop);
  audio.addEventListener?.('error', drop);
}

/** Alle laufenden fremden Sounds stoppen (z. B. beim Verlassen des Sprachkanals). */
export function stopSoundboardSounds(): void {
  for (const audio of active.splice(0)) {
    try {
      audio.pause();
    } catch {
      /* ignorieren */
    }
  }
}

/**
 * Für das Socket-Ereignis SOUNDBOARD_PLAY: spielt den Sound mit der gemerkten lokalen Lautstärke – nur wenn
 * dieser Client mit genau diesem Sprachkanal verbunden und nicht taub geschaltet ist.
 */
export function playSoundboardEvent(p: { channelId: string; url: string; volume: number }): HTMLAudioElement | null {
  const vs = voiceState();
  if (vs.channelId !== p.channelId || vs.deafened) return null;
  const v = effectiveVolume(p.volume);
  if (v <= 0) return null;
  const audio = playAudio(p.url, v);
  if (audio) track(audio);
  return audio;
}

/** Dauer kurz anzeigen: „0,8 s", „2,5 s", „12 s". */
export function formatSoundDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '–';
  const s = ms / 1000;
  return `${(s < 10 ? s.toFixed(1) : String(Math.round(s))).replace('.', ',')} s`;
}

export function filterSounds(sounds: readonly SoundDto[], query: string): SoundDto[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...sounds];
  return sounds.filter((s) => s.name.toLowerCase().includes(q) || (s.emoji ?? '').includes(q));
}

/** Fehler beim Abspielen verständlich machen. */
export function soundboardErrorText(err: unknown, external: boolean): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Zu viele Sounds – bitte kurz warten.';
    // Andere 403-Codes (z. B. „nicht im Sprachkanal") tragen eine eigene, verständliche Server-Meldung.
    if (err.status === 403 && err.code === 'MISSING_PERMISSIONS') {
      const perms = (err.body.details as { permissions?: unknown } | undefined)?.permissions;
      if (Array.isArray(perms) && perms.includes('USE_EXTERNAL_SOUNDS')) return 'Dir fehlt das Recht „Externe Sounds verwenden" in diesem Kanal.';
      return external
        ? 'Dir fehlt das Recht „Externe Sounds verwenden" (oder „Soundboard verwenden") in diesem Kanal.'
        : 'Dir fehlt das Recht „Soundboard verwenden" in diesem Kanal.';
    }
    if (err.status === 404) return 'Sound oder Kanal nicht gefunden.';
  }
  return errorMessage(err);
}

export const SOUNDBOARD_COOLDOWN_MS = 1000;
